import { useState, useEffect } from 'react';
import { RequireEvents } from '../components/DependencyGate';
import {
  Datagrid,
  List,
  Create,
  Edit,
  TextField,
  TextInput,
  NumberInput,
  NumberField,
  Show,
  TabbedShowLayout,
  Tab,
  SelectInput,
  SaveButton,
  DeleteButton,
  Toolbar,
  FunctionField,
  usePermissions,
  useRecordContext,
  useRefresh,
  useNotify,
  useGetList,
  useGetMany,
  useCreate,
  Link,
  required,
} from 'react-admin';
import { useController, useWatch } from 'react-hook-form';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { TabbedForm } from '../components/TabbedForm';
import { TimeUnitToggleRow } from '../components/TimeUnitToggleRow';
import {
  ArchivedSelect,
  CourseField,
  CourseIdSelect,
  CourseSelect,
} from '../components/selects';
import { AppBreadcrumbs } from '../components/Breadcrumbs';
import { useCourseIds } from '../utils/useCourseIds';
import { can, hasRole, type Permissions, type Event as EventType, type ScoreTable, type Assessment as AssessmentType } from '../types';
import { HP_DOMAINS } from '../constants/hpDomains';
import { assessmentReadiness, eventScoringReadiness, type AssessmentReadiness } from '../utils/readiness';
import { hasDemographicScoreFields } from '../utils/scoreTableHelpers';
import { apiFetch } from '../providers/api-rest-provider';
import { eventTransform } from './events-helpers';

import { hasDuplicates, validateAssessmentEvents, selectedEventIds, noOptionsState, assessmentStatusChipProps } from './assessments-helpers';

// Re-exported for existing importers (assessment_instances historically
// imported hasDuplicates from this module).
export { hasDuplicates, validateAssessmentEvents };

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

const AssessmentReadinessChip = ({ readiness }: { readiness: string }) => {
  if (readiness === 'draft') return <Chip size="small" label="Draft" color="default" />;
  if (readiness === 'needs_scoring') return <Chip size="small" label="Needs scoring" color="warning" />;
  if (readiness === 'ready') return <Chip size="small" label="Ready" color="success" />;
  return null;
};

const AssessmentStatusChip = ({ status }: { status?: string }) => {
  const props = assessmentStatusChipProps(status);
  return <Chip label={props.label} color={props.color} size="small" />;
};
AssessmentStatusChip.displayName = 'AssessmentStatusChip';

const AdvanceRetractButtons = () => {
  const record = useRecordContext<any>();
  const refresh = useRefresh();
  const notify = useNotify();
  const { permissions } = usePermissions<any>();
  const canAdvance = hasRole(permissions, 'course_director');
  if (!record || !canAdvance) return null;
  const status = record.status ?? 'draft';
  const action = async (path: string) => {
    try {
      await apiFetch(`assessments/${record.id}/${path}`, { method: 'POST' });
      refresh();
      notify(`Assessment ${path === 'advance' ? 'advanced' : 'retracted'} successfully`, { type: 'success' });
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : 'Action failed', { type: 'error' });
    }
  };
  return (
    <Box display="flex" gap={1} mt={1} mb={2}>
      <AssessmentStatusChip status={status} />
      {status !== 'published' && (
        <Button variant="contained" size="small" onClick={() => action('advance')}>
          {status === 'draft' ? 'Advance to Ready' : 'Publish'}
        </Button>
      )}
      {status !== 'draft' && (
        <Button variant="outlined" size="small" color="warning" onClick={() => action('retract')}>
          Retract
        </Button>
      )}
    </Box>
  );
};
AdvanceRetractButtons.displayName = 'AdvanceRetractButtons';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

const AssessmentList = () => {
  const course_ids = useCourseIds();
  const { permissions } = usePermissions<Permissions>();

  return (
    <RequireEvents>
    <List
      exporter={false}
      filter={course_ids ? { course_id: course_ids } : undefined}
      filters={[
        <CourseIdSelect key="course" alwaysOn />,
        <ArchivedSelect key="archived" />,
      ]}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid
        rowClick={can(permissions, 'manage_assessments') ? 'edit' : 'show'}
      >
        <TextField label="Assessment Name" source="name" />
        <CourseField />
        <FunctionField label="Status" render={(record: any) => <AssessmentStatusChip status={record?.status} />} />
      </Datagrid>
    </List>
    </RequireEvents>
  );
};

// ---------------------------------------------------------------------------
// AssessmentEventsTab
// ---------------------------------------------------------------------------

const AssessmentEventsTab = ({
  events,
  scoreTables,
  record,
}: {
  events: EventType[];
  scoreTables: ScoreTable[];
  record: AssessmentType | null | undefined;
}) => {
  const assessmentEventIds: string[] = (record?.events ?? []).map((e: any) => e.event_id);

  const orderedEvents = assessmentEventIds
    .map((id) => events.find((e) => e.id === id))
    .filter((e): e is EventType => !!e);

  if (orderedEvents.length === 0) {
    return (
      <Box mt={2}>
        <Alert severity="info">
          This assessment has no events yet. Add events using the Edit form.
        </Alert>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Stack spacing={2}>
        {orderedEvents.map((event) => {
          const eventTables = scoreTables.filter((st) => st.event_id === event.id);
          const readiness = eventScoringReadiness(event, eventTables);
          const chipProps =
            readiness === 'complete'
              ? { color: 'success' as const, label: 'Scoring complete' }
              : readiness === 'needs_table'
                ? { color: 'warning' as const, label: 'Needs scoring table' }
                : { color: 'default' as const, label: 'Pass-Fail' };

          return (
            <Card key={event.id} variant="outlined">
              <CardActionArea component={Link} to={`/events/${event.id}/show`}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {event.name}
                    </Typography>
                    <Chip size="small" {...chipProps} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {event.measurements?.length
                      ? `${event.measurements.length} measurement(s)`
                      : event.scoring_type ?? 'No measurements'}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// AssessmentScoringTab
// ---------------------------------------------------------------------------

const AssessmentScoringTab = ({
  events,
  scoreTables,
  readiness,
  coverage,
  permissions,
  assessmentRecord,
}: {
  events: EventType[];
  scoreTables: ScoreTable[];
  readiness: string;
  coverage: { covered: number; total: number };
  permissions: Permissions | undefined;
  assessmentRecord: AssessmentType | null | undefined;
}) => {
  const canWrite = can(permissions, 'manage_assessments');
  const eventIds: string[] = (assessmentRecord?.events ?? []).map((e: any) => e.event_id);
  const orderedEvents = eventIds
    .map((id) => events.find((e) => e.id === id))
    .filter((e): e is EventType => !!e);

  const bannerText =
    readiness === 'draft'
      ? 'No events yet.'
      : readiness === 'ready'
        ? `Scoring complete: ${coverage.total === 0 ? 'all events are Pass/Fail' : `${coverage.covered} of ${coverage.total} events covered`}.`
        : `${coverage.total - coverage.covered} event(s) missing scoring tables — attempts will record without points.`;
  const bannerSeverity: 'success' | 'info' | 'warning' =
    readiness === 'ready' ? 'success' : readiness === 'draft' ? 'info' : 'warning';

  return (
    <Box mt={2}>
      <Alert severity={bannerSeverity} sx={{ mb: 2 }}>
        {bannerText}
      </Alert>

      {orderedEvents.map((event) => {
        const eventTables = scoreTables.filter((st) => st.event_id === event.id);
        const eventRead = eventScoringReadiness(event, eventTables);
        const activeTables = eventTables.filter(
          (st) => st.status === 'active' && !hasDemographicScoreFields(st.rows),
        );
        const measurements: any[] = event.measurements ?? [];

        return (
          <Box key={event.id} mb={3}>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Link to={`/events/${event.id}/show`}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {event.name}
                </Typography>
              </Link>
              {eventRead === 'no_table_needed' && (
                <Chip size="small" color="default" label="Pass/Fail" />
              )}
              {eventRead === 'complete' && <Chip size="small" color="success" label="Scored" />}
              {eventRead === 'needs_table' && (
                <Chip size="small" color="warning" label="Missing table" />
              )}
            </Stack>

            {eventRead === 'no_table_needed' ? (
              <Typography variant="body2" color="text.secondary">
                Pass/Fail event — no scoring table needed.
              </Typography>
            ) : measurements.length === 0 ? (
              <Alert severity="warning">Event has no measurements defined.</Alert>
            ) : (
              measurements.map((m: any) => {
                const mActive = activeTables.find(
                  (st) => st.measurement_id === m.measurement_id,
                );
                return (
                  <Box key={m.measurement_id} pl={2} mb={1}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body2">{m.name}</Typography>
                      {mActive ? (
                        <>
                          <Chip size="small" color="success" label="Active table" />
                          <Typography variant="caption" color="text.secondary">
                            {mActive.label} · Effective {mActive.effective_date ?? '—'} ·{' '}
                            {mActive.rows?.length ?? 0} rows
                          </Typography>
                          <Link to={`/score_tables/${mActive.id}/show`}>
                            <Typography variant="caption">View →</Typography>
                          </Link>
                        </>
                      ) : (
                        <>
                          <Chip size="small" color="warning" label="No active table" />
                          {canWrite && (
                            <Link
                              to={`/score_tables/create?event_id=${event.id}&measurement_id=${m.measurement_id}`}
                            >
                              <Typography variant="caption">Create →</Typography>
                            </Link>
                          )}
                        </>
                      )}
                    </Stack>
                  </Box>
                );
              })
            )}
            <Divider sx={{ mt: 1 }} />
          </Box>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// AssessmentScheduleTab
// ---------------------------------------------------------------------------

const AssessmentScheduleTab = ({
  instances,
  assessmentRecord,
  readiness,
  events,
  scoreTables,
  permissions,
}: {
  instances: any[];
  assessmentRecord: AssessmentType | null | undefined;
  readiness: string;
  events: EventType[];
  scoreTables: ScoreTable[];
  permissions: Permissions | undefined;
}) => {
  const [warnOpen, setWarnOpen] = useState(false);
  const canSchedule = can(permissions, 'schedule_instances');

  const notReadyEvents = events.filter((ev) => {
    const tables = scoreTables.filter((st) => st.event_id === ev.id);
    return eventScoringReadiness(ev, tables) === 'needs_table';
  });

  const doNavigate = () => {
    window.location.hash = `/assessment_instances/create?assessment_id=${assessmentRecord?.id}`;
  };

  const handleScheduleClick = () => {
    if (readiness === 'needs_scoring') {
      setWarnOpen(true);
    } else {
      doNavigate();
    }
  };

  const handleProceed = () => {
    setWarnOpen(false);
    doNavigate();
  };

  return (
    <Box mt={2}>
      {canSchedule && (
        <Box mb={2}>
          <Button variant="contained" onClick={handleScheduleClick}>
            Schedule this assessment
          </Button>
        </Box>
      )}

      {/* Warn-and-allow dialog */}
      <Dialog open={warnOpen} onClose={() => setWarnOpen(false)}>
        <DialogTitle>Schedule without complete scoring?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            The following events have no active scoring table. Attempts will be recorded without
            points:
          </Typography>
          <ul>
            {notReadyEvents.map((ev) => (
              <li key={ev.id}>
                <Typography>{ev.name}</Typography>
              </li>
            ))}
          </ul>
          <Typography>
            You may still schedule the assessment and add scoring tables later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarnOpen(false)}>Cancel</Button>
          <Button onClick={handleProceed} variant="contained">
            Proceed anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instances list */}
      {instances.length === 0 ? (
        <Alert severity="info">No instances scheduled yet.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Status</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {instances.map((inst: any) => (
              <TableRow key={inst.id}>
                <TableCell>
                  {inst.date ? new Date(inst.date).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>{inst.student_group_id ?? '—'}</TableCell>
                <TableCell>{inst.started_at ? 'In use' : 'Scheduled'}</TableCell>
                <TableCell>
                  <Link to={`/assessment_instances/${inst.id}/show`}>View</Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// AssessmentResultsTab
// ---------------------------------------------------------------------------

const AssessmentResultsTab = ({ assessmentId }: { assessmentId?: string }) => {
  const { data: tallies = [] } = useGetList('tallies', {
    filter: { assessment_id: assessmentId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'ASC' },
  });

  if (tallies.length === 0) {
    return (
      <Box mt={2}>
        <Alert severity="info">
          No tallies computed yet. Use POST /tallies/compute to generate results for each student.
        </Alert>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Student</TableCell>
            <TableCell align="right">Total Points</TableCell>
            <TableCell>Complete</TableCell>
            <TableCell>Computed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tallies.map((t: any) => (
            <TableRow key={t.id}>
              <TableCell>{t.student_id ?? '—'}</TableCell>
              <TableCell align="right">{t.total_points ?? 0}</TableCell>
              <TableCell>
                {t.complete === false ? (
                  <Tooltip title={`Missing tables: ${(t.missing_tables ?? []).join(', ')}`}>
                    <Chip size="small" color="warning" label="Incomplete" />
                  </Tooltip>
                ) : (
                  <Chip size="small" color="success" label="Complete" />
                )}
              </TableCell>
              <TableCell>{t.computed_at ? new Date(t.computed_at).toLocaleDateString() : '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// AssessmentHubLayout — rendered inside <Show>
// ---------------------------------------------------------------------------

const AssessmentHubLayout = () => {
  const record = useRecordContext<AssessmentType>();
  const { permissions } = usePermissions<Permissions>();

  const eventIds: string[] = (record?.events ?? []).map((e: any) => e.event_id).filter(Boolean);

  const { data: eventsData = [] } = useGetMany<EventType>(
    'events',
    { ids: eventIds },
    { enabled: eventIds.length > 0 },
  );

  const { data: allScoreTables = [] } = useGetList<ScoreTable>(
    'score_tables',
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'effective_date', order: 'DESC' },
    },
    { enabled: eventIds.length > 0 },
  );

  const scoreTables = allScoreTables.filter((st) => eventIds.includes(st.event_id));

  const { readiness, coverage } = record
    ? assessmentReadiness(record, eventsData, scoreTables)
    : { readiness: 'draft' as AssessmentReadiness, coverage: { covered: 0, total: 0 } };

  const { data: instances = [] } = useGetList(
    'assessment_instances',
    {
      filter: { assessment_id: record?.id },
      pagination: { page: 1, perPage: 25 },
      sort: { field: 'date', order: 'DESC' },
    },
    { enabled: !!record?.id },
  );

  return (
    <Box p={2}>
      <AppBreadcrumbs
        items={[
          { label: 'Assessments', to: '/assessments' },
          { label: record?.name ?? 'Assessment' },
        ]}
      />

      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Typography variant="h5">{record?.name}</Typography>
        <AssessmentReadinessChip readiness={readiness} />
        {instances.length > 0 && <Chip size="small" label="Scheduled" color="info" />}
      </Stack>

      <AdvanceRetractButtons />

      <TabbedShowLayout>
        <Tab label="Overview">
          <TextField source="name" />
          <CourseField />
          <NumberField label="Duration in minutes" source="duration" />
          <NumberField source="team_size" label="Members per Team" />
          <NumberField source="student_ratio" label="Students per Instructor" />
        </Tab>

        <Tab label="Events">
          <AssessmentEventsTab
            events={eventsData}
            scoreTables={scoreTables}
            record={record}
          />
        </Tab>

        <Tab label="Scoring">
          <AssessmentScoringTab
            events={eventsData}
            scoreTables={scoreTables}
            readiness={readiness}
            coverage={coverage}
            permissions={permissions}
            assessmentRecord={record}
          />
        </Tab>

        <Tab label="Schedule">
          <AssessmentScheduleTab
            instances={instances}
            assessmentRecord={record}
            readiness={readiness}
            events={eventsData}
            scoreTables={scoreTables}
            permissions={permissions}
          />
        </Tab>

        <Tab label="Results">
          <AssessmentResultsTab assessmentId={record?.id as string | undefined} />
        </Tab>
      </TabbedShowLayout>
    </Box>
  );
};

const AssessmentShow = () => (
  <Show>
    <AssessmentHubLayout />
  </Show>
);

// ---------------------------------------------------------------------------
// Create / Edit
// ---------------------------------------------------------------------------

const AssessmentCreate = () => (
  <Create>
    <AssessmentForm />
  </Create>
);

const AssessmentEdit = () => (
  <Edit mutationMode="pessimistic">
    <AssessmentForm />
  </Edit>
);

const AssessmentForm = () => (
  <TabbedForm
    labels={['Basic', 'Events']}
    toolbar={<AssessmentToolbar />}
    warnWhenUnsavedChanges
  >
    <DetailsPage />
    <EventsPage />
  </TabbedForm>
);

const DetailsPage = () => (
  <Box>
    <TextInput fullWidth label="Assessment Name" source="name" validate={required()} />
    <SelectInput
      fullWidth
      label="Assessment Type"
      source="type"
      choices={[...HP_DOMAINS]}
      helperText="Human-performance domain (per DoD Total Force Fitness)"
      validate={required()}
    />
    <CourseSelect />
    <NumberInput
      fullWidth
      defaultValue={60}
      label="Duration in minutes"
      source="duration"
    />
    <NumberInput fullWidth source="team_size" label="Members per Team" />
    <NumberInput
      fullWidth
      source="student_ratio"
      label="Students per Instructor"
    />
  </Box>
);

// ---------------------------------------------------------------------------
// Unit schemes hook (for inline dialog)
// ---------------------------------------------------------------------------

interface UnitSchemeEntry {
  id: string;
  kind: string;
  label: string;
  base_unit: string;
}

function useUnitSchemes() {
  const [schemes, setSchemes] = useState<UnitSchemeEntry[]>([]);
  useEffect(() => {
    apiFetch('unit-schemes').then(setSchemes).catch(() => {});
  }, []);
  return schemes;
}

// ---------------------------------------------------------------------------
// Inline event creation dialog (Task 1.6)
// ---------------------------------------------------------------------------

const MEASUREMENT_KINDS = [
  { id: 'time', name: 'Time (compound)' },
  { id: 'distance', name: 'Distance (compound)' },
  { id: 'quantity', name: 'Quantity / Weight' },
];

const AGGREGATION_TYPES = [
  { id: 'best', name: 'Best (highest or fastest)' },
  { id: 'cumulative', name: 'Cumulative (sum of all)' },
  { id: 'average', name: 'Average (mean of all)' },
];

const TEAM_SCORING_CHOICES = [
  { id: 'single_result', name: 'One result for the whole team' },
  { id: 'aggregate', name: 'Each member records their own result' },
];

interface CreateEventDialogProps {
  open: boolean;
  prefilledName: string;
  onClose: () => void;
  onCreated: (event: EventType) => void;
}

const CreateEventDialog = ({ open, prefilledName, onClose, onCreated }: CreateEventDialogProps) => {
  const [create, { isPending }] = useCreate();
  const allSchemes = useUnitSchemes();

  const [name, setName] = useState('');
  const [scoringMode, setScoringMode] = useState<'measured' | 'pass_fail'>('measured');
  const [kind, setKind] = useState('time');
  const [aggregation, setAggregation] = useState('best');
  const [unitSchemeId, setUnitSchemeId] = useState('');
  const [lowerIsBetter, setLowerIsBetter] = useState(true);
  const [participation, setParticipation] = useState<'individual' | 'team'>('individual');
  const [teamSizeExact, setTeamSizeExact] = useState<number | ''>(2);
  const [teamScoring, setTeamScoring] = useState('single_result');
  const [dlgError, setDlgError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setName(prefilledName);
      setScoringMode('measured');
      setKind('time');
      setAggregation('best');
      setUnitSchemeId('');
      setLowerIsBetter(true);
      setParticipation('individual');
      setTeamSizeExact(2);
      setTeamScoring('single_result');
      setDlgError(null);
    }
  }, [open, prefilledName]);

  const handleSave = () => {
    if (!name.trim()) {
      setDlgError('Event name is required.');
      return;
    }
    if (scoringMode === 'measured' && kind !== 'pass_fail' && !unitSchemeId) {
      setDlgError('Select at least one time unit (or choose a unit for this kind).');
      return;
    }
    if (participation === 'team') {
      const n = Number(teamSizeExact);
      if (!Number.isInteger(n) || n < 2) {
        setDlgError('Team size must be an integer ≥ 2.');
        return;
      }
      if (!teamScoring) {
        setDlgError('Team scoring model is required.');
        return;
      }
    }

    const body = eventTransform({
      name: name.trim(),
      scoring_mode: scoringMode,
      ...(scoringMode === 'measured' ? {
        measurements: [{
          kind,
          aggregation,
          unit_scheme_id: unitSchemeId || undefined,
          lower_is_better: lowerIsBetter,
        }],
      } : {}),
      participation,
      ...(participation === 'team' ? {
        team_size: { exact: Number(teamSizeExact) },
        team_scoring: teamScoring,
      } : {}),
    });

    setDlgError(null);
    create('events', { data: body }, {
      onSuccess: (data: any) => {
        onCreated(data as EventType);
      },
      onError: (err: any) => {
        if (err?.status === 403) {
          setDlgError('Permission denied — event creation requires the course_director role.');
        } else {
          setDlgError(err?.message ?? 'Failed to create event. Check the form and try again.');
        }
      },
    });
  };

  const distanceQuantitySchemes = allSchemes.filter(s => s.kind === kind);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create new event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <MuiTextField
            label="Event Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            required
          />

          <Box>
            <Typography variant="caption" color="text.secondary">Scoring mode</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {(['measured', 'pass_fail'] as const).map((m) => (
                <Button
                  key={m}
                  variant={scoringMode === m ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setScoringMode(m)}
                >
                  {m === 'measured' ? 'Measured' : 'Go/No-Go'}
                </Button>
              ))}
            </Stack>
          </Box>

          {scoringMode === 'measured' && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">Measurement kind</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                  {MEASUREMENT_KINDS.map(mk => (
                    <Button
                      key={mk.id}
                      variant={kind === mk.id ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => { setKind(mk.id); setUnitSchemeId(''); }}
                    >
                      {mk.name}
                    </Button>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">Aggregation</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                  {AGGREGATION_TYPES.map(ag => (
                    <Button
                      key={ag.id}
                      variant={aggregation === ag.id ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setAggregation(ag.id)}
                    >
                      {ag.name}
                    </Button>
                  ))}
                </Stack>
              </Box>

              {kind === 'time' ? (
                <TimeUnitToggleRow value={unitSchemeId} onChange={setUnitSchemeId} />
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">Units</Typography>
                  <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                    {distanceQuantitySchemes.map(s => (
                      <Button
                        key={s.id}
                        variant={unitSchemeId === s.id ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setUnitSchemeId(s.id)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}

              <Box>
                <Typography variant="caption" color="text.secondary">Direction</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Button
                    variant={lowerIsBetter ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setLowerIsBetter(true)}
                  >
                    Lower is better (e.g. time)
                  </Button>
                  <Button
                    variant={!lowerIsBetter ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => setLowerIsBetter(false)}
                  >
                    Higher is better (e.g. distance)
                  </Button>
                </Stack>
              </Box>
            </>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary">Participation</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {(['individual', 'team'] as const).map(p => (
                <Button
                  key={p}
                  variant={participation === p ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setParticipation(p)}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Button>
              ))}
            </Stack>
          </Box>

          {participation === 'team' && (
            <>
              <MuiTextField
                label="Team size (exact)"
                type="number"
                value={teamSizeExact}
                onChange={(e) => setTeamSizeExact(e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 2 }}
                size="small"
                fullWidth
              />
              <Box>
                <Typography variant="caption" color="text.secondary">Team scoring model</Typography>
                <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
                  {TEAM_SCORING_CHOICES.map(ts => (
                    <Button
                      key={ts.id}
                      variant={teamScoring === ts.id ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setTeamScoring(ts.id)}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    >
                      {ts.name}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {dlgError && (
            <Alert severity="error">{dlgError}</Alert>
          )}

          <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
            The scoring table (anchors and grade curve) is authored separately on the Event's own page
            after creation.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={isPending}>
          {isPending ? 'Creating…' : 'Create event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// EventsPage — atomic picker (Task 1)
// ---------------------------------------------------------------------------

type EventOption = EventType | { __isCreate: true };

const EventsPage = () => {
  const { field, fieldState: { error } } = useController({
    name: 'events',
    defaultValue: [],
    rules: { validate: (v) => validateAssessmentEvents(v) ?? true },
  });

  const currentEvents: Array<{ event_id: string }> = field.value ?? [];
  const selIds = selectedEventIds(currentEvents);

  const { data: selectedEventData = [] } = useGetMany<EventType>(
    'events',
    { ids: selIds },
    { enabled: selIds.length > 0 },
  );

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPrefilledName, setDialogPrefilledName] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(inputValue), 300);
    return () => clearTimeout(t);
  }, [inputValue]);

  // Unfiltered total — needed to distinguish empty-catalog from no-match
  const { total: totalInCatalog } = useGetList<EventType>(
    'events',
    { pagination: { page: 1, perPage: 1 }, sort: { field: 'name', order: 'ASC' } },
  );

  const { data: searchResults = [], isFetching } = useGetList<EventType>(
    'events',
    {
      filter: { q: searchQuery },
      pagination: { page: 1, perPage: 20 },
      sort: { field: 'name', order: 'ASC' },
    },
  );

  const options: EventOption[] = searchResults.filter((e) => !selIds.includes(e.id));
  const state = noOptionsState(isFetching, totalInCatalog, searchQuery);

  const noOptionsText =
    state === 'loading' ? 'Searching…' :
    state === 'empty_catalog' ? (
      <Typography variant="body2">
        No events exist yet —{' '}
        <Link to="/events/create">create one under Assessment Events first</Link>
      </Typography>
    ) :
    state === 'no_match' ? `No events match "${searchQuery}"` :
    'Type to search events';

  const handleAdd = (evt: EventType) => {
    field.onChange([...currentEvents, { event_id: evt.id }]);
    setInputValue('');
  };

  const handleCreated = (evt: EventType) => {
    field.onChange([...currentEvents, { event_id: evt.id }]);
    setDialogOpen(false);
    setInputValue('');
  };

  const handleRemove = (eventId: string) => {
    field.onChange(currentEvents.filter((e) => e.event_id !== eventId));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const next = [...currentEvents];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    field.onChange(next);
  };

  const handleMoveDown = (index: number) => {
    if (index === currentEvents.length - 1) return;
    const next = [...currentEvents];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    field.onChange(next);
  };

  return (
    <Box>
      {/* Selected events ordered list */}
      {currentEvents.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No events selected yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {currentEvents.map((ev, i) => {
            const eventData = selectedEventData.find((e) => e.id === ev.event_id);
            return (
              <Box
                key={ev.event_id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Typography sx={{ minWidth: 28, fontWeight: 700, color: 'text.secondary' }}>
                  {i + 1}.
                </Typography>
                <Typography sx={{ flex: 1 }}>
                  {eventData?.name ?? ev.event_id}
                </Typography>
                {eventData && (
                  <Chip
                    size="small"
                    label={eventData.scoring_mode === 'pass_fail' ? 'Go/No-Go' : (eventData.scoring_mode ?? 'measured')}
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => handleMoveUp(i)}
                  disabled={i === 0}
                  aria-label="Move up"
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveDown(i)}
                  disabled={i === currentEvents.length - 1}
                  aria-label="Move down"
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleRemove(ev.event_id)}
                  color="error"
                  aria-label="Remove event"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Inline validation message */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>{error.message}</Alert>
      )}

      {/* Add event combobox — always below the list, never inside it */}
      <Autocomplete<EventOption>
        options={options}
        getOptionLabel={(opt) => {
          if ('__isCreate' in opt) return `Create event "${inputValue}"…`;
          return typeof opt === 'string' ? opt : ((opt as EventType).name ?? '');
        }}
        isOptionEqualToValue={(a, b) => {
          if ('__isCreate' in a || '__isCreate' in b) return false;
          return (a as EventType).id === (b as EventType).id;
        }}
        filterOptions={(opts) => {
          if (inputValue.trim() && opts.filter(o => !('__isCreate' in o)).length === 0) {
            return [{ __isCreate: true as const }, ...opts.filter(o => !('__isCreate' in o))];
          }
          return opts;
        }}
        inputValue={inputValue}
        onInputChange={(_, value, reason) => {
          // Preserve typed text when user clicks away without selecting
          if (reason !== 'reset') setInputValue(value);
        }}
        onChange={(_: React.SyntheticEvent, opt: EventOption | null) => {
          if (!opt) return;
          if ('__isCreate' in opt) {
            setDialogPrefilledName(inputValue);
            setDialogOpen(true);
            return;
          }
          handleAdd(opt as EventType);
        }}
        value={null}
        clearOnBlur={false}
        blurOnSelect
        noOptionsText={noOptionsText}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            label="Add event"
            size="small"
            placeholder="Type to search events…"
          />
        )}
      />

      <CreateEventDialog
        open={dialogOpen}
        prefilledName={dialogPrefilledName}
        onClose={() => setDialogOpen(false)}
        onCreated={handleCreated}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Assessment save toolbar with disabled-state tooltip (Task 2)
// ---------------------------------------------------------------------------

const AssessmentToolbar = () => {
  const currentEvents = useWatch({ name: 'events' });
  const validationError = validateAssessmentEvents(currentEvents);
  const record = useRecordContext();
  const { permissions } = usePermissions<Permissions>();

  return (
    <Toolbar sx={{ justifyContent: 'space-between' }}>
      <Tooltip
        title={validationError ?? ''}
        disableHoverListener={!validationError}
        arrow
      >
        <span>
          <SaveButton disabled={!!validationError} />
        </span>
      </Tooltip>
      <Box>
        <SaveButton
          label={record?.archived_at ? 'Unarchive' : 'Archive'}
          variant="text"
          type="button"
          transform={(data: any) => ({
            ...data,
            archived_at: record?.archived_at ? null : new Date().toISOString(),
          })}
        />
        {can(permissions, 'manage_assessments') && <DeleteButton />}
      </Box>
    </Toolbar>
  );
};

export const assessments = {
  list: AssessmentList,
  show: AssessmentShow,
  create: AssessmentCreate,
  edit: AssessmentEdit,
};
